<template>
  <main class="profile-page">
    <div class="container">
      <button type="button" class="back" @click="$router.back()">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5m6-6-6 6 6 6"/></svg>
        Retour
      </button>

      <div v-if="loading" class="state" aria-busy="true" aria-live="polite">
        <span class="loader"></span> Chargement du profil...
      </div>

      <div v-else-if="error" class="state state-error" role="alert">
        <h2>Oups…</h2>
        <p>{{ error }}</p>
      </div>

      <template v-else-if="profile">
        <!-- ============ CARTE PROFIL ============ -->
        <section class="hero-card">
          <div class="cover" aria-hidden="true">
            <svg class="cover-cross" viewBox="-34 -40 453 752" fill="none" stroke="currentColor" stroke-width="22" stroke-linejoin="round">
              <path d="M100 40 Q190 98 285 40 Q256 112 238 188 Q300 170 355 165 Q330 245 355 345 Q300 322 238 304 Q250 450 255 600 Q285 622 290 645 Q192 598 100 645 Q130 622 130 600 Q135 450 146 304 Q90 322 30 345 Q55 245 30 165 Q90 170 146 188 Q128 112 100 40 Z"/>
              <circle cx="192" cy="245" r="70"/>
            </svg>
          </div>

          <div class="identity">
            <div class="avatar">
              <img v-if="imageUrl(profile.profileImage)" :src="imageUrl(profile.profileImage)" :alt="`Photo de ${profile.name}`" />
              <span v-else>{{ initial }}</span>
            </div>

            <div class="who">
              <span class="badge" :class="`badge-${(profile.accountType || 'Personal').toLowerCase()}`">
                <TypeIcon :type="profile.accountType || 'Personal'" class="badge-icon" />
                {{ typeLabel(profile.accountType) }}
              </span>
              <h1>{{ profile.name }}</h1>
              <p v-if="formatDate(profile.createdAt)" class="since">
                Membre depuis le {{ formatDate(profile.createdAt) }}
              </p>
            </div>

            <router-link v-if="profile.isMe" to="/account" class="me-btn">
              Modifier mon compte
            </router-link>
          </div>

          <dl class="stats">
            <div class="stat">
              <dt>{{ profile.publishedRequestsCount }}</dt>
              <dd>demande{{ profile.publishedRequestsCount > 1 ? "s" : "" }} d'aide publiée{{ profile.publishedRequestsCount > 1 ? "s" : "" }}</dd>
            </div>
          </dl>
        </section>

        <!-- ============ DEMANDES PUBLIÉES ============ -->
        <section class="requests">
          <h2>Demandes d'aide publiées</h2>

          <div v-if="profile.requests.length" class="list">
            <router-link
              v-for="r in profile.requests"
              :key="r.publicId"
              :to="`/requests/${r.publicId}`"
              class="request-card"
            >
              <div class="request-top">
                <span class="urgency" :class="(r.urgency || 'Normal').toLowerCase()">
                  <i class="dot"></i> {{ r.urgency || "Normal" }}
                </span>
                <small>{{ formatDate(r.createdAt) }}</small>
              </div>
              <h3>{{ r.name }}</h3>
              <p>{{ r.message }}</p>
              <span class="more">Voir les détails →</span>
            </router-link>
          </div>

          <div v-else class="empty">
            <h3>Aucune demande publiée</h3>
            <p>Ce membre n'a pas de demande d'aide en cours.</p>
          </div>
        </section>
      </template>
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import axios from "axios"
import API_URL from "@/config/api"
import TypeIcon from "@/components/TypeIcon.vue"

const route = useRoute()
const router = useRouter()

const profile = ref(null)
const loading = ref(true)
const error = ref("")

const initial = computed(() =>
  String(profile.value?.name || "?").trim().charAt(0).toUpperCase()
)

const imageUrl = (path) => {
  if (!path) return null
  return path.startsWith("http") ? path : `${API_URL}${path}`
}

const typeLabel = (value) =>
  ({ Personal: "Personnel", Association: "Association", Business: "Commerce", Other: "Autre" }[value] || "Personnel")

const formatDate = (value) => {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
}

const loadProfile = async () => {
  loading.value = true
  error.value = ""

  try {
    const { data } = await axios.get(`${API_URL}/api/users/${route.params.id}/profile`)
    profile.value = data
  } catch (e) {
    if (e.response?.status === 401) {
      router.push("/login")
      return
    }
    error.value =
      e.response?.status === 404
        ? "Ce membre est introuvable."
        : e.response?.data?.message || "Impossible de charger ce profil."
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  let stored = null
  try {
    stored = JSON.parse(localStorage.getItem("user"))
  } catch {
    stored = null
  }

  if (!stored?.token) {
    router.push("/login")
    return
  }

  loadProfile()
})
</script>

<style scoped>
.profile-page {
  --red: #d90012;
  --red-deep: #8f0010;
  --blue: #0033a0;
  --orange: #f2a800;
  --green: #16a34a;
  --ink: #1c1511;
  --ink-soft: #3a2f28;
  --muted: #6e6259;
  --paper: #faf6ef;
  --line: rgba(28, 21, 17, 0.12);
  --serif: "Fraunces", "Noto Serif Armenian", Georgia, serif;
  --sans: "Instrument Sans", "Inter", system-ui, sans-serif;

  min-height: 100vh;
  padding: 120px clamp(16px, 5vw, 72px) 90px;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
}
.profile-page *,
.profile-page *::before,
.profile-page *::after {
  box-sizing: border-box;
}
.container {
  max-width: 860px;
  margin: 0 auto;
}

.back {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 46px;
  margin-bottom: 26px;
  padding: 0 22px 0 18px;
  border: 1.5px solid var(--ink);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--ink);
  font: 600 14px/1 var(--sans);
  cursor: pointer;
  transition: background 0.25s ease, color 0.25s ease, transform 0.25s ease;
}
.back:hover {
  background: var(--ink);
  color: var(--paper);
  transform: translateY(-2px);
}

.state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--muted);
}
.state-error {
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: 28px;
  background: #fff;
  text-align: center;
}
.state-error h2 { margin: 0; font: 600 30px var(--serif); color: var(--ink); }
.state-error p { margin: 0; color: var(--red-deep); font-weight: 600; }
.loader {
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(217, 0, 18, 0.25);
  border-top-color: var(--red);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ---------- carte profil ---------- */
.hero-card {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 32px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(28, 21, 17, 0.08);
}
.cover {
  position: relative;
  height: 130px;
  overflow: hidden;
  background: var(--ink);
}
.cover::before {
  position: absolute;
  inset: 0 0 auto;
  height: 5px;
  background: linear-gradient(90deg, var(--red) 0 42%, var(--blue) 42% 72%, var(--orange) 72%);
  content: "";
}
.cover-cross {
  position: absolute;
  top: -16px;
  right: 7%;
  width: 110px;
  color: rgba(242, 168, 0, 0.22);
}

.identity {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  padding: 0 clamp(20px, 4vw, 40px);
  margin-top: -52px;
}
.avatar {
  display: grid;
  width: 112px;
  height: 112px;
  flex-shrink: 0;
  place-items: center;
  overflow: hidden;
  border: 5px solid #fff;
  border-radius: 50%;
  background: var(--ink);
  box-shadow: 0 14px 30px rgba(28, 21, 17, 0.2);
  color: var(--orange);
  font: 700 44px/1 var(--serif);
}
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.who {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding-bottom: 6px;
}
.who h1 {
  margin: 0;
  font: 600 clamp(28px, 4vw, 40px) / 1.1 var(--serif);
  letter-spacing: -0.025em;
  overflow-wrap: anywhere;
}
.since {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
}
.me-btn {
  align-self: center;
  padding: 12px 20px;
  border: 1.5px solid var(--ink);
  border-radius: 999px;
  color: var(--ink);
  font: 700 14px var(--sans);
  text-decoration: none;
  transition: background 0.25s ease, color 0.25s ease;
}
.me-btn:hover {
  background: var(--ink);
  color: #fff;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font: 700 11px/1.2 var(--sans);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.badge-icon { font-size: 14px; }
.badge-personal { background: #eef2ff; color: var(--blue); }
.badge-association { background: #fff1ef; color: var(--red); }
.badge-business { background: #fff6dd; color: #8a5d00; }
.badge-other { background: #f3ecdf; color: var(--ink-soft); }

.stats {
  display: flex;
  gap: 14px;
  margin: 28px clamp(20px, 4vw, 40px) clamp(22px, 4vw, 36px);
  padding-top: 24px;
  border-top: 1px solid var(--line);
}
.stat {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.stat dt {
  font: 600 38px/1 var(--serif);
  color: var(--red);
}
.stat dd {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
}

/* ---------- demandes ---------- */
.requests { margin-top: 44px; }
.requests h2 {
  margin: 0 0 20px;
  font: 600 28px var(--serif);
  letter-spacing: -0.02em;
}
.list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}
.request-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  border: 1px solid var(--line);
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 10px 26px rgba(28, 21, 17, 0.06);
  color: inherit;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.request-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 38px rgba(28, 21, 17, 0.12);
}
.request-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.request-top small { color: var(--muted); font-size: 12.5px; }
.request-card h3 { margin: 0; font-size: 18px; }
.request-card p {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  color: var(--ink-soft);
  font-size: 14.5px;
  line-height: 1.6;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.more {
  margin-top: auto;
  color: var(--red);
  font-size: 14px;
  font-weight: 700;
}

.urgency {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(28, 21, 17, 0.05);
  font: 700 12px/1.2 var(--sans);
}
.urgency .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--green);
}
.urgency.important .dot { background: var(--orange); }
.urgency.urgent .dot { background: var(--red); }

.empty {
  padding: 46px 20px;
  border: 1px dashed rgba(28, 21, 17, 0.25);
  border-radius: 24px;
  text-align: center;
}
.empty h3 { margin: 0 0 8px; font: 600 22px var(--serif); }
.empty p { margin: 0; color: var(--muted); }

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 560px) {
  .identity {
    flex-direction: column;
    align-items: flex-start;
  }
  .me-btn { align-self: flex-start; }
}

@media (prefers-reduced-motion: reduce) {
  * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}
</style>